import { Box, useTheme } from '@mui/material';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

/**
 * Compact area chart for the metrics page. Two series stacked:
 * pass (success-tone) on top of fail (danger-tone). Tabular-nums in the
 * tooltip so digit columns don't jitter as the cursor moves.
 *
 * This isn't a generic chart — it's the one shape we use for the metrics
 * page. A second variant for transaction-latency would land alongside.
 */

interface TrendPoint {
  date: string;
  pass: number;
  fail: number;
}

export function TrendChart({ data, height = 220 }: { data: TrendPoint[]; height?: number }) {
  const theme = useTheme();
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="passFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={theme.palette.status.success} stopOpacity={0.32} />
              <stop offset="100%" stopColor={theme.palette.status.success} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="failFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={theme.palette.status.danger} stopOpacity={0.32} />
              <stop offset="100%" stopColor={theme.palette.status.danger} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.palette.border.subtle} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="date"
            stroke={theme.palette.text.tertiary}
            tick={{ fontSize: 11 }}
            tickFormatter={(v: string) => v.slice(5)}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            stroke={theme.palette.text.tertiary}
            tick={{ fontSize: 11 }}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            width={32}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.surface.inverse,
              border: 'none',
              borderRadius: theme.radius.sm,
              color: theme.palette.text.onInverse,
              fontSize: 12,
              fontVariantNumeric: 'tabular-nums',
            }}
            labelStyle={{ color: theme.palette.text.onInverse }}
            cursor={{ stroke: theme.palette.border.strong, strokeDasharray: '3 3' }}
          />
          <Area
            type="monotone"
            dataKey="pass"
            stackId="1"
            stroke={theme.palette.status.success}
            strokeWidth={1.5}
            fill="url(#passFill)"
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="fail"
            stackId="1"
            stroke={theme.palette.status.danger}
            strokeWidth={1.5}
            fill="url(#failFill)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

export type { TrendPoint };
